import React, { useState, useEffect, createContext, useContext } from 'react';
import { Menu, Icon, Sidebar as SemanticSidebar, Image, Popup, Button } from 'semantic-ui-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useQuiz } from '../../context/QuizContext';
import GettelLogo from '../../images/logo-gettel.png'; // Caminho atualizado para a logo

// Contexto para compartilhar estado do sidebar
interface SidebarContextType {
  isCollapsed: boolean;
  sidebarWidth: string;
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  sidebarWidth: '200px'
});

export const useSidebar = () => useContext(SidebarContext);

// Interface para itens do menu
interface MenuItem {
  path: string;
  name: string;
  icon: string;
  roles?: string[]; // Opcional - se não definido, acessível para todos autenticados
  /** Visível só para o papel Admin. */
  requiresPosVenda?: boolean;
  submenu?: MenuItem[]; // Para submenus
  preserveSubmenu?: boolean; // Não achata grupo de um único filho
}

const CLIENTES_ROLES = ['Admin', 'Manager', 'User', 'Seller', 'BKO', 'Tester', 'TeamLeader'];
const CONTRATOS_ROLES = ['Admin', 'Manager', 'User', 'Seller', 'BKO', 'Tester', 'TeamLeader'];
const PLANOS_ROLES = ['Admin', 'Manager', 'User', 'Seller', 'BKO', 'Tester', 'TeamLeader'];

// Definição dos itens do menu
const menuItems: MenuItem[] = [
  {
    path: '/dashboard',
    name: 'Dashboard',
    icon: 'dashboard',
    roles: ['Admin', 'Manager', 'User', 'BKO', 'Tester', 'TeamLeader'] // Seller não vê Dashboard na sidebar
  },
  {
    path: '/contracts',
    name: 'Contratos',
    icon: 'file alternate',
    roles: CONTRATOS_ROLES
  },
  {
    path: '/cadastros',
    name: 'Cadastros',
    icon: 'folder',
    submenu: [
      {
        path: '/catalogo',
        name: 'Catálogo',
        icon: 'cubes',
        roles: PLANOS_ROLES,
        submenu: [
          {
            path: '/plans',
            name: 'Planos',
            icon: 'list alternate',
            roles: PLANOS_ROLES
          },
          {
            path: '/aparelhos',
            name: 'Aparelhos',
            icon: 'mobile alternate',
            roles: ['Admin', 'TeamLeader']
          },
          {
            path: '/icCids',
            name: 'ICCIDs',
            icon: 'microchip',
            roles: ['Admin']
          }
        ]
      },
      {
        path: '/menu-clientes',
        name: 'Clientes',
        icon: 'building',
        roles: CLIENTES_ROLES,
        submenu: [
          {
            path: '/clients',
            name: 'Clientes',
            icon: 'building',
            roles: CLIENTES_ROLES
          },
          {
            path: '/simulation',
            name: 'Simulação',
            icon: 'calculator',
            roles: ['Admin', 'Manager', 'Seller', 'TeamLeader']
          },
          {
            path: '/contracts/client-hub',
            name: 'Histórico cliente',
            icon: 'history',
            roles: ['Admin']
          }
        ]
      }
    ]
  },
  {
    path: '/pos-venda',
    name: 'Pós-venda',
    icon: 'headphones',
    requiresPosVenda: true,
    preserveSubmenu: true,
    submenu: [
      {
        path: '/pos-venda',
        name: 'Protocolos',
        icon: 'tasks',
        requiresPosVenda: true,
      },
      {
        path: '/pos-venda/envios',
        name: 'Pendentes a enviar',
        icon: 'truck',
        requiresPosVenda: true,
      },
      {
        path: '/pos-venda/confirmacao-endereco',
        name: 'Fluxo logístico',
        icon: 'sitemap',
        requiresPosVenda: true,
      },
      {
        path: '/pos-venda/emails',
        name: 'E-mails enviados',
        icon: 'envelope outline',
        requiresPosVenda: true,
      },
      {
        path: '/pos-venda/cancelamentos-do-dia',
        name: 'Cancelamentos do dia',
        icon: 'calendar check outline',
        requiresPosVenda: true,
      },
      {
        path: '/suspensions',
        name: 'Linhas a Suspender',
        icon: 'pause circle',
        roles: ['Admin'],
        submenu: [
          {
            path: '/suspensions',
            name: 'Dashboard',
            icon: 'table',
            roles: ['Admin']
          },
          {
            path: '/suspensions/dia',
            name: 'Contratos a suspender hoje',
            icon: 'robot',
            roles: ['Admin']
          }
        ]
      }
    ],
  },
  {
    path: '/sla-vtme',
    name: 'SLA VTME',
    icon: 'wait',
    roles: ['Admin'],
    submenu: [
      {
        path: '/backoffice',
        name: 'Backoffice',
        icon: 'briefcase',
        roles: ['Admin'],
        preserveSubmenu: true,
        submenu: [
          {
            path: '/filas-vtme',
            name: 'Filas VTME',
            icon: 'clock',
            roles: ['Admin']
          }
        ]
      },
      {
        path: '/cqv',
        name: 'CQV',
        icon: 'clipboard',
        roles: ['Admin'],
        submenu: [
          {
            path: '/cqv/atribuicoes-vtme',
            name: 'Atribuições VTME',
            icon: 'mail',
            roles: ['Admin']
          },
          {
            path: '/cqv/escala',
            name: 'Escala',
            icon: 'calendar alternate',
            roles: ['Admin']
          }
        ]
      }
    ]
  },
  {
    path: '/coleta/operacao',
    name: 'Coleta P2B/Lemmit',
    icon: 'search',
    roles: ['Admin'],
    preserveSubmenu: true,
    submenu: [
      {
        path: '/coleta/logins',
        name: 'Logins',
        icon: 'key',
        roles: ['Admin']
      },
      {
        path: '/coleta/operacao',
        name: 'Operação',
        icon: 'search',
        roles: ['Admin']
      }
    ]
  },
  {
    path: '/administracao',
    name: 'Administração',
    icon: 'sitemap',
    roles: ['Admin'],
    submenu: [
      {
        path: '/campaigns/manage',
        name: 'Gestão de Campanhas',
        icon: 'list',
        roles: ['Admin']
      },
      {
        path: '/campaigns/dashboard',
        name: 'Dashboard de Campanhas',
        icon: 'chart line',
        roles: ['Admin']
      },
      /* Módulo Comparar Campanhas desativado temporariamente
      {
        path: '/campaigns/compare',
        name: 'Comparar',
        icon: 'chart bar',
        roles: ['Admin']
      }
      */
      {
        path: '/teams',
        name: 'Times',
        icon: 'users',
        roles: ['Admin']
      },
      {
        path: '/users',
        name: 'Usuários',
        icon: 'user',
        roles: ['Admin']
      },
      {
        path: '/access-management',
        name: 'Gestão de Acessos',
        icon: 'lock',
        roles: ['Admin']
      },
      {
        path: '/authorized-devices',
        name: 'Computadores autorizados',
        icon: 'desktop',
        roles: ['Admin']
      },
      {
        path: '/authorized-devices/exemptions',
        name: 'Liberação por usuário',
        icon: 'unlock alternate',
        roles: ['Admin']
      },
      {
        path: '/contracts/operations',
        name: 'Parâmetros contratos',
        icon: 'settings',
        roles: ['Admin']
      }
    ]
  },
  // Atividades oculto no menu (rota continua ativa)
  // {
  //   path: '/activities',
  //   name: 'Atividades',
  //   icon: 'industry',
  //   roles: ['Admin']
  // },
  // {
  //   path: '/roles',
  //   name: 'Papéis',
  //   icon: 'shield',
  //   roles: ['Admin']
  // },
  {
    path: '/campaigns/my-performance',
    name: 'Minhas Metas',
    icon: 'target',
    roles: ['Seller', 'TeamLeader']
  },
  {
    path: '/training',
    name: 'Treinamento',
    icon: 'graduation cap',
    roles: ['Admin'],
    submenu: [
      {
        path: '/learning/quizzes/manage',
        name: 'Gestão de Questionários',
        icon: 'edit',
        roles: ['Admin']
      },
      {
        path: '/training/dashboard',
        name: 'Dashboard de Treinamento',
        icon: 'chart bar',
        roles: ['Admin']
      }
    ]
  },
  // WhatsApp oculto no menu (rotas continuam ativas)
  // {
  //   path: '/whatsapp',
  //   name: 'WhatsApp',
  //   icon: 'whatsapp',
  //   roles: ['Admin', 'Manager', 'Seller'],
  //   submenu: [
  //     {
  //       path: '/whatsapp/dashboard',
  //       name: 'Dashboard',
  //       icon: 'chart bar',
  //       roles: ['Admin', 'Manager']
  //     },
  //     {
  //       path: '/whatsapp/batches',
  //       name: 'Envio de Lotes',
  //       icon: 'paper plane',
  //       roles: ['Admin', 'Manager', 'Seller']
  //     },
  //     {
  //       path: '/whatsapp/clients',
  //       name: 'Clientes',
  //       icon: 'users',
  //       roles: ['Admin', 'Manager']
  //     },
  //     {
  //       path: '/whatsapp/templates',
  //       name: 'Templates',
  //       icon: 'file alternate',
  //       roles: ['Admin', 'Manager']
  //     },
  //     {
  //       path: '/whatsapp/attendants',
  //       name: 'Atendimentos',
  //       icon: 'headphones',
  //       roles: ['Admin', 'Manager', 'Seller']
  //     }
  //   ]
  // },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { hasAnyRole, isAuthenticated, hasRole } = useAuth();
  const temAcessoPosVenda = hasRole('Admin');
  const { isQuizInProgress } = useQuiz();
  
  // Estado para controlar se o sidebar está colapsado
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Estado para controlar quais submenus estão expandidos
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('expandedSubmenus');
    return saved ? new Set(JSON.parse(saved)) : new Set(['/training']); // Por padrão, Treinamento expandido
  });

  // Salvar preferência no localStorage quando mudar
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Salvar submenus expandidos no localStorage
  useEffect(() => {
    localStorage.setItem('expandedSubmenus', JSON.stringify(Array.from(expandedSubmenus)));
  }, [expandedSubmenus]);

  // Abre o grupo da rota atual (e o grupo pai, se houver) sem fechar os outros.
  useEffect(() => {
    const pathname = location.pathname;
    const matches = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
    const containsPath = (item: MenuItem): boolean =>
      !!item.submenu?.some((sub) => matches(sub.path) || containsPath(sub));
    const pathsToOpen: string[] = [];
    const walk = (items: MenuItem[]) => {
      items.forEach((item) => {
        if (!item.submenu?.length || !containsPath(item)) return;
        pathsToOpen.push(item.path);
        walk(item.submenu);
      });
    };
    walk(menuItems);
    if (pathsToOpen.length === 0) return;
    setExpandedSubmenus((prev) => {
      if (pathsToOpen.every((path) => prev.has(path))) return prev;
      const next = new Set(prev);
      pathsToOpen.forEach((path) => next.add(path));
      return next;
    });
  }, [location.pathname]);

  const toggleSubmenu = (path: string) => {
    setExpandedSubmenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    // Disparar evento customizado para notificar outros componentes
    window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: { isCollapsed: newState } }));
  };

  const canSeeItem = (item: MenuItem) => {
    if (item.requiresPosVenda) return isAuthenticated && temAcessoPosVenda;
    if (!item.roles) return isAuthenticated;
    return hasAnyRole(item.roles);
  };

  const topLevelSortLabel = (item: MenuItem) =>
    item.path === '/campaigns/my-performance' && hasRole('TeamLeader')
      ? 'Meta da Equipe'
      : item.name;

  // Filtrar itens do menu baseado nas roles do usuário.
  // Grupo com um único subitem visível vira item solto (ex.: Seller vê Planos, não Catálogo).
  // Dashboard fica fixo no topo; o restante segue o rótulo visível, sem acento.
  const normalizeMenuItem = (item: MenuItem): MenuItem | null => {
    if (!canSeeItem(item)) return null;
    if (!item.submenu) return item;
    const visibleSubmenu = item.submenu
      .map(normalizeMenuItem)
      .filter((child): child is MenuItem => child !== null);
    if (visibleSubmenu.length === 0) return null;
    if (visibleSubmenu.length === 1 && !item.preserveSubmenu) return visibleSubmenu[0];
    return { ...item, submenu: visibleSubmenu };
  };

  const visibleMenuItems = menuItems
    .map(normalizeMenuItem)
    .filter((item): item is MenuItem => item !== null)
    .sort((a, b) => {
    if (a.path === '/dashboard') return -1;
    if (b.path === '/dashboard') return 1;
    return topLevelSortLabel(a).localeCompare(topLevelSortLabel(b), 'pt-BR', { sensitivity: 'base' });
  });

  const getMenuItemLabel = (item: MenuItem) => {
    if (item.path === '/campaigns/my-performance') {
      return hasRole('TeamLeader') ? 'Meta da Equipe' : 'Minhas Metas';
    }
    return item.name;
  };

  const isPathActive = (path: string) => {
    if (path === '/contracts') {
      const pathname = location.pathname;
      if (pathname.startsWith('/contracts/client-hub') || pathname.startsWith('/contracts/operations')) {
        return false;
      }
    }
    return location.pathname.startsWith(path);
  };

  const sidebarWidth = isCollapsed ? '60px' : '200px';

  return (
    <SidebarContext.Provider value={{ isCollapsed, sidebarWidth }}>
    <SemanticSidebar
      as={Menu}
      animation="push"
      inverted
      vertical
      visible
      style={{
        position: 'fixed',
        top: '42px', // Ajustado para a altura provável do Header
        left: 0,
        height: 'calc(100vh - 42px)', // Ajustado para a altura da tela menos o Header
        zIndex: 1000,
        width: sidebarWidth,
        background: '#1a1a1a',
        backdropFilter: 'blur(10px)',
        boxShadow: '4px 0 24px rgba(176, 196, 222, 0.2)',
        border: 'none',
        transition: 'width 0.3s ease'
      }}
    >
      {/* Botão de Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: isCollapsed ? 'center' : 'flex-end',
        padding: '10px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Button
          icon={isCollapsed ? 'chevron right' : 'chevron left'}
          size="small"
          inverted
          circular
          onClick={toggleSidebar}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'rgba(255, 255, 255, 0.9)',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e: any) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e: any) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
          }}
        />
      </div>
      <Popup
        content="Você tem um questionário em andamento. Finalize ou cancele antes de navegar."
        disabled={!isQuizInProgress}
        trigger={
          <Menu.Item 
            as={isQuizInProgress ? 'div' : Link} 
            to={isQuizInProgress ? undefined : "/dashboard"} 
            style={{ 
              marginBottom: '20px', 
              marginTop: '10px',
              opacity: isQuizInProgress ? 0.5 : 1,
              cursor: isQuizInProgress ? 'not-allowed' : 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: isCollapsed ? '10px' : '10px 16px'
            }}
            onClick={(e: any) => {
              if (isQuizInProgress) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {isCollapsed ? (
              <Image src={GettelLogo} style={{ width: '40px', height: 'auto' }} />
            ) : (
              <Image src={GettelLogo} centered />
            )}
          </Menu.Item>
        }
      />

      {visibleMenuItems.map((item) => {
        // Se tem submenu, renderizar como dropdown
        if (item.submenu) {
          const visibleSubmenu = item.submenu.filter(subItem => {
            if (!subItem.roles) return true;
            return hasAnyRole(subItem.roles);
          });

          if (visibleSubmenu.length === 0) return null;

          // Se colapsado, não mostrar submenus (ou mostrar apenas o header com tooltip)
          if (isCollapsed) {
            const collapsedTarget = item.preserveSubmenu ? (visibleSubmenu[0]?.path ?? item.path) : undefined;
            return (
              <Popup
                key={item.path}
                content={item.preserveSubmenu ? visibleSubmenu[0]?.name ?? getMenuItemLabel(item) : getMenuItemLabel(item)}
                position="right center"
                mouseEnterDelay={300}
                trigger={
                  <Menu.Item
                    as={collapsedTarget && !isQuizInProgress ? Link : undefined}
                    to={collapsedTarget && !isQuizInProgress ? collapsedTarget : undefined}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '12px',
                      minHeight: '44px',
                      margin: '2px 8px',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e: any) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseLeave={(e: any) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Icon name={item.icon as any} />
                  </Menu.Item>
                }
              />
            );
          }

          const isExpanded = expandedSubmenus.has(item.path);

          return (
            <Menu.Item key={item.path} style={{ padding: 0 }}>
              <Menu.Header 
                onClick={() => toggleSubmenu(item.path)}
                style={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  margin: '8px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e: any) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e: any) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon name={item.icon as any} />
                  <span style={{ marginLeft: '8px' }}>{getMenuItemLabel(item)}</span>
                </div>
                <Icon 
                  name={isExpanded ? 'chevron down' : 'chevron right'} 
                  style={{ 
                    marginLeft: 'auto',
                    transition: 'transform 0.3s ease'
                  }} 
                />
              </Menu.Header>
              {isExpanded && visibleSubmenu.map((subItem) => {
                const renderLeaf = (leaf: MenuItem, marginLeft: string) => (
                  <Popup
                    key={leaf.path}
                    content="Você tem um questionário em andamento. Finalize ou cancele antes de navegar."
                    disabled={!isQuizInProgress}
                    trigger={
                      <Menu.Item
                        as={isQuizInProgress ? 'div' : Link}
                        to={isQuizInProgress ? undefined : leaf.path}
                        active={isPathActive(leaf.path)}
                        style={{
                          color: isQuizInProgress ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.8)',
                          transition: 'all 0.3s ease',
                          borderRadius: '6px',
                          margin: `2px 8px 2px ${marginLeft}`,
                          fontSize: '13px',
                          padding: '8px 12px',
                          cursor: isQuizInProgress ? 'not-allowed' : 'pointer',
                          opacity: isQuizInProgress ? 0.5 : 1
                        }}
                        onMouseEnter={(e: any) => {
                          if (!isQuizInProgress) {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseLeave={(e: any) => {
                          if (!isQuizInProgress) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                          }
                        }}
                        onClick={(e: any) => {
                          if (isQuizInProgress) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                      >
                        <Icon name={leaf.icon as any} size="small" />
                        {leaf.name}
                      </Menu.Item>
                    }
                  />
                );

                if (subItem.submenu && subItem.submenu.length > 0) {
                  const nestedOpen = expandedSubmenus.has(subItem.path);
                  return (
                    <React.Fragment key={subItem.path}>
                      <Menu.Header
                        onClick={() => toggleSubmenu(subItem.path)}
                        style={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          padding: '8px 12px',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          margin: '2px 8px 2px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderRadius: '6px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon name={subItem.icon as any} size="small" />
                          <span style={{ marginLeft: '8px' }}>{subItem.name}</span>
                        </div>
                        <Icon name={nestedOpen ? 'chevron down' : 'chevron right'} />
                      </Menu.Header>
                      {nestedOpen && subItem.submenu.map((leaf) => renderLeaf(leaf, '40px'))}
                    </React.Fragment>
                  );
                }

                return renderLeaf(subItem, '24px');
              })}
            </Menu.Item>
          );
        }

        // Item normal sem submenu
        const menuItemContent = (
          <Menu.Item
            as={isQuizInProgress ? 'div' : Link}
            to={isQuizInProgress ? undefined : item.path}
            active={isPathActive(item.path)}
            style={{
              color: isQuizInProgress ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)',
              transition: 'all 0.3s ease',
              borderRadius: '8px',
              margin: '2px 8px',
              cursor: isQuizInProgress ? 'not-allowed' : 'pointer',
              opacity: isQuizInProgress ? 0.5 : 1,
              display: 'flex',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              alignItems: 'center',
              padding: isCollapsed ? '12px' : '12px 16px',
              minHeight: '44px'
            }}
            onMouseEnter={(e: any) => {
              if (!isQuizInProgress) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = 'white';
              }
            }}
            onMouseLeave={(e: any) => {
              if (!isQuizInProgress) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)';
              }
            }}
            onClick={(e: any) => {
              if (isQuizInProgress) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Icon name={item.icon as any} style={{ margin: 0, marginRight: isCollapsed ? 0 : '10px' }} />
            {!isCollapsed && <span>{getMenuItemLabel(item)}</span>}
          </Menu.Item>
        );

        // Se colapsado, mostrar tooltip com nome do item
        if (isCollapsed) {
          return (
            <Popup
              key={item.path}
              content={getMenuItemLabel(item)}
              position="right center"
              trigger={menuItemContent}
              mouseEnterDelay={300}
              style={{
                zIndex: 10001
              }}
            />
          );
        }

        // Se expandido, mostrar popup apenas para quiz em progresso
        return (
          <Popup
            key={item.path}
            content="Você tem um questionário em andamento. Finalize ou cancele antes de navegar."
            disabled={!isQuizInProgress}
            trigger={menuItemContent}
          />
        );
      })}
    </SemanticSidebar>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
